import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminGenerationMenuItem, GeneratedImage, User } from '../types';
import { fetchMenuById } from '../services/api/menus';
import { fetchPublicTimeline } from '../services/api/library';
import PublicTimelineView from './PublicTimelineView';
import { SparklesIcon } from '../components/icons/HeroIcons';
import { myGarageLogin } from '../services/api/mygarage-auth';

interface MenuLandingPageProps {
  onLoginSuccess?: (user: User) => void;
  onOpenSmsLogin?: () => void;
}

/**
 * メニューIDでアクセスした場合のランディングページ
 * ログイン前でもメニュー説明と公開タイムラインを表示
 * 右側にログインフォームを表示
 */
const MenuLandingPage: React.FC<MenuLandingPageProps> = ({
  onLoginSuccess,
  onOpenSmsLogin,
}) => {
  const { menuId } = useParams<{ menuId: string }>();
  const navigate = useNavigate();
  const [menu, setMenu] = useState<AdminGenerationMenuItem | null>(null);
  const [publicImages, setPublicImages] = useState<GeneratedImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ログインフォーム用のstate
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [savePassword, setSavePassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // 保存されたユーザー名を復元（パスワードはブラウザ自動入力に任せる）
  useEffect(() => {
    const savedUsername = localStorage.getItem('mygarage-saved-username');
    if (savedUsername) {
      setLoginEmail(savedUsername);
      setSavePassword(true);
    }
    // 旧バージョンで保存されたパスワードを削除
    localStorage.removeItem('mygarage-saved-password');
  }, []);

  // メニュー情報でページタイトルとdescriptionを設定
  useEffect(() => {
    if (menu) {
      // ページタイトルを設定
      document.title = `AISHA ${menu.name} - by MyGarage`;

      // meta descriptionを設定
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', menu.description || `${menu.name} - AIを使った車画像生成サービス AISHA`);
    }

    // クリーンアップ: コンポーネントがアンマウントされたらデフォルトに戻す
    return () => {
      document.title = 'AISHA by MyGarage';
    };
  }, [menu]);

  useEffect(() => {
    const loadData = async () => {
      if (!menuId) {
        setError('メニューIDが指定されていません');
        setIsLoading(false);
        return;
      }

      const menuIdNum = parseInt(menuId, 10);
      if (isNaN(menuIdNum)) {
        setError('無効なメニューIDです');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // メニュー情報と公開タイムラインを並行取得
        const [menuData, timelineData] = await Promise.all([
          fetchMenuById(menuIdNum),
          fetchPublicTimeline(menuIdNum),
        ]);

        if (!menuData) {
          setError('メニューが見つかりません');
          setIsLoading(false);
          return;
        }

        setMenu(menuData);
        setPublicImages(timelineData);
        setError(null);
      } catch (err) {
        console.error('データ取得エラー:', err);
        setError('データの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [menuId]);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('メールアドレスとパスワードを入力してください');
      return;
    }

    setIsLoggingIn(true);
    setLoginError(null);

    try {
      const user = await myGarageLogin(loginEmail, loginPassword);

      if (user) {
        // ユーザー名のみ保存（パスワードはブラウザ自動入力に任せる）
        if (savePassword) {
          localStorage.setItem('mygarage-saved-username', loginEmail);
        } else {
          localStorage.removeItem('mygarage-saved-username');
        }

        if (onLoginSuccess) {
          onLoginSuccess(user);
        }
        // ページをリロードしてログイン後の状態にする
        window.location.reload();
      } else {
        setLoginError('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
      }
    } catch (err) {
      console.error('ログインエラー:', err);
      setLoginError('ログインに失敗しました。もう一度お試しください。');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSmsLogin = () => {
    if (onOpenSmsLogin) {
      onOpenSmsLogin();
    } else {
      // App.tsxにSMSログインモーダルを開くリクエストを送る
      navigate('/?openSmsLogin=true');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400 mx-auto mb-4"></div>
          <p className="text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            トップへ戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-gray-900">
      {/* メインコンテンツ：2カラムレイアウト（ログイン後と同じ構造） */}
      {menu && (
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* 左側：メニュー説明（ログイン後と同じスタイル） */}
            <div className="lg:w-1/2 bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-lg p-6 border border-indigo-700/30">
              {/* 1. メニュー名 */}
              <h2 className="text-2xl font-bold text-white mb-2">{menu.name}</h2>
              {/* 2. メニュー説明 */}
              {menu.description && (
                <p className="text-gray-300 leading-relaxed">{menu.description}</p>
              )}
              {/* 3. 使い方説明画像 */}
              {(menu.usageGuideImageUrl || menu.usage_guide_image_url) && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-2">使い方</p>
                  <img
                    src={menu.usageGuideImageUrl || menu.usage_guide_image_url}
                    alt="使い方説明"
                    className="max-w-lg w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              )}
              {/* 4. サンプル画像（横並び） */}
              {(menu.sampleInputImgUrl || menu.sampleResultImgUrl) && (
                <div className="mt-4 flex flex-wrap gap-6">
                  {menu.sampleInputImgUrl && (
                    <div className="flex flex-col items-center">
                      <p className="text-sm text-gray-400 mb-2">入力画像例</p>
                      <img
                        src={menu.sampleInputImgUrl}
                        alt="サンプル入力"
                        className="max-w-xs h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                  {menu.sampleResultImgUrl && (
                    <div className="flex flex-col items-center">
                      <p className="text-sm text-gray-400 mb-2">生成結果例</p>
                      <img
                        src={menu.sampleResultImgUrl}
                        alt="サンプル結果"
                        className="max-w-xs h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                </div>
              )}
              {/* 5. 消費クレジット */}
              <div className="mt-4 flex items-center gap-2">
                <span className="text-gray-400 text-sm">消費クレジット:</span>
                <span className="text-lg font-bold text-yellow-400">{menu.credit}</span>
              </div>
            </div>

            {/* 右側：ログインフォーム */}
            <div className="lg:w-1/2">
              {/* 新規登録セクション */}
              <div className="bg-gray-800 rounded-lg p-6 mb-4 text-center">
                {/* アイコン */}
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>

                <h3 className="text-xl font-bold text-white mb-3">アカウントをお持ちでない方</h3>

                <p className="text-gray-400 text-sm mb-6">MyGarageアカウントの作成が必要です。</p>

                {/* ステップ（センター寄せ） */}
                <div className="flex flex-col items-center mb-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center text-sm text-gray-300">1</span>
                    <span className="text-gray-300">アカウント新規作成</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center text-sm text-gray-300">2</span>
                    <span className="text-gray-300">愛車登録（ガレージ作成）</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-sm text-white">3</span>
                    <span className="text-white font-medium">AISHA 利用開始</span>
                  </div>
                </div>

                <a
                  href="https://shakoba.jp/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full bg-red-500 hover:bg-red-400 text-white text-lg font-bold px-8 py-3 rounded-full transition-colors"
                >
                  アカウント新規作成
                </a>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 shadow-xl text-center">
                {/* アイコン */}
                <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                </div>

                <h3 className="text-xl font-bold text-white mb-3">MyGarageアカウントをお持ちの方</h3>

                <p className="text-gray-400 text-sm mb-1">お持ちのアカウントでログインするだけで、</p>
                <p className="text-gray-400 text-sm mb-6">すぐにAISHAを始められます。</p>

                {loginError && (
                  <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">
                    {loginError}
                  </div>
                )}

                <div className="space-y-4 text-left">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">メールアドレス</label>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="example@mygarage.jp"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">パスワード</label>
                    <input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="********"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="savePassword"
                      checked={savePassword}
                      onChange={(e) => setSavePassword(e.target.checked)}
                      className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-indigo-500"
                    />
                    <label htmlFor="savePassword" className="ml-2 text-sm text-gray-400">
                      ログインパスワードを保存する
                    </label>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    MyGarageアカウントのメールアドレスでログインします
                  </p>

                  <button
                    onClick={handleLogin}
                    disabled={isLoggingIn}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:from-gray-600 disabled:to-gray-600 text-white py-3 rounded-lg font-medium transition-all shadow-lg"
                  >
                    {isLoggingIn ? '認証中...' : 'MyGarageでログイン'}
                  </button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-800 text-gray-400">開発用</span>
                    </div>
                  </div>

                  <button
                    onClick={handleSmsLogin}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 text-white py-3 rounded-lg font-medium transition-all"
                  >
                    電話番号でログイン (AWS SMS)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 公開タイムラインセクション */}
      <section className="container mx-auto px-4 py-8">
        <h3 className="text-xl font-bold text-indigo-400 mb-6">
          {menu?.name ? `「${menu.name}」で作成された作品` : '公開作品'}
        </h3>
        {publicImages.length > 0 ? (
          <PublicTimelineView publicImages={publicImages} currentUser={null} hideTitle />
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">
              まだこのメニューで公開された作品はありません
            </p>
          </div>
        )}
      </section>

      {/* フッター */}
      <footer className="bg-gray-800/50 border-t border-gray-700 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            AISHA By MyGarage - AIを使った車画像生成サービス
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MenuLandingPage;
